/**
 * FacilitatorPortalService
 * Facilitator self-service portal: my registrations, my sessions, dashboard stats,
 * participants and session progress updates
 */
import { BaseService } from '../base/BaseService';
/**
 * Service for the facilitator portal (current authenticated facilitator)
 */
export class FacilitatorPortalService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? FacilitatorPortalService.DEFAULT_SERVICE_PATH,
            basePath: FacilitatorPortalService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    /**
     * Get registrations the current facilitator is assigned to
     * @returns Array of facilitator registration views
     */
    async myRegistrations() {
        const response = await this.myRegistrationsRaw();
        return response.data ?? [];
    }
    /**
     * Get registrations the current facilitator is assigned to with full response
     * @returns ResponseData with array of facilitator registration views
     */
    myRegistrationsRaw() {
        const url = this.buildUrl('my-registrations');
        return this.http.get(url);
    }
    /**
     * Get the current facilitator's registrations filtered by a RegistrationStatus.
     * Scoped to the logged-in facilitator's UserId server-side.
     */
    async myRegistrationsByStatus(statusId) {
        const response = await this.myRegistrationsByStatusRaw(statusId);
        return response.data ?? [];
    }
    myRegistrationsByStatusRaw(statusId) {
        const url = this.buildUrl(`my-registrations/by-status/${statusId}`);
        return this.http.get(url);
    }
    /**
     * Get the current facilitator's registrations still awaiting a start — status 0 (Pending).
     * The facilitator twin of `leaderPortal.myPendingCases()`; drives the Pending Enrolments page
     * without it having to hard-code the status id.
     */
    async myPendingRegistrations() {
        const response = await this.myPendingRegistrationsRaw();
        return response.data ?? [];
    }
    myPendingRegistrationsRaw() {
        const url = this.buildUrl('my-registrations/pending');
        return this.http.get(url);
    }
    /**
     * Get the current facilitator's active registrations whose next session is already past
     * @returns Array of facilitator registration views, most overdue first
     */
    async myBehindScheduleRegistrations() {
        const response = await this.myBehindScheduleRegistrationsRaw();
        return response.data ?? [];
    }
    /**
     * Get the current facilitator's behind-schedule registrations with full response
     * @returns ResponseData with array of facilitator registration views
     */
    myBehindScheduleRegistrationsRaw() {
        const url = this.buildUrl('my-registrations/behind-schedule');
        return this.http.get(url);
    }
    /**
     * Get the current facilitator's sessions with required custom-form fields still blank
     * @returns Array of session form-gap rows, oldest session first
     */
    async myIncompleteSessionForms() {
        const response = await this.myIncompleteSessionFormsRaw();
        return response.data ?? [];
    }
    /**
     * Get the current facilitator's incomplete session forms with full response
     * @returns ResponseData with array of session form-gap rows
     */
    myIncompleteSessionFormsRaw() {
        const url = this.buildUrl('my-sessions/incomplete-forms');
        return this.http.get(url);
    }
    /**
     * Stream my registrations in batches (fetch-once, chunk client-side — portal lists are small).
     * Yields one `FacilitatorRegistrationView[]` per batch so the UI can paint progressively.
     */
    async *streamMyRegistrations(chunkSize = 50) {
        const all = await this.myRegistrations();
        for (let i = 0; i < all.length; i += chunkSize) {
            yield all.slice(i, i + chunkSize);
        }
    }
    /** Stream my registrations filtered by a RegistrationStatus, in batches. */
    async *streamMyRegistrationsByStatus(statusId, chunkSize = 50) {
        const all = await this.myRegistrationsByStatus(statusId);
        for (let i = 0; i < all.length; i += chunkSize) {
            yield all.slice(i, i + chunkSize);
        }
    }
    /**
     * Get a single one of my registrations, by ID.
     * GET api/portal/facilitator/my-registrations/{registrationId}
     */
    async myRegistration(registrationId) {
        const res = await this.http.get(this.buildUrl(`my-registrations/${registrationId}`));
        if (!res.data)
            throw new Error(`Registration ${registrationId} not found`);
        return res.data;
    }
    /**
     * Change only the status of a registration I run.
     * PATCH api/portal/facilitator/registrations/{registrationId}/change-status/{newStatus}
     */
    async changeRegistrationStatus(registrationId, newStatus) {
        const res = await this.http.patch(this.buildUrl(`registrations/${registrationId}/change-status/${newStatus}`));
        if (!res.data)
            throw new Error(`Failed to change status for registration ${registrationId}`);
        return res.data;
    }
    /**
     * Decline an enrolment I was assigned to. Records the reason as an office-only registration
     * note (hidden from facilitators) and removes me from the registration's assigned facilitators,
     * in one step. The course-side twin of `leaderPortal.declineCase()`.
     *
     * `reason` is required — a blank/whitespace reason is rejected with 400. A 403 means I'm not
     * currently one of this registration's facilitators. If I was the lead, the registration is
     * simply left with no lead (the call still succeeds so the office can reassign).
     *
     * POST api/portal/facilitator/registrations/{registrationId}/decline
     */
    async declineRegistration(registrationId, data) {
        const res = await this.declineRegistrationRaw(registrationId, data);
        if (!res.data)
            throw new Error(`Failed to decline registration ${registrationId}`);
        return res.data;
    }
    declineRegistrationRaw(registrationId, data) {
        const url = this.buildUrl(`registrations/${registrationId}/decline`);
        return this.http.post(url, data);
    }
    /**
     * Get sessions the current facilitator runs
     * @returns Array of facilitator session views
     */
    async mySessions(registrationId) {
        const response = await this.mySessionsRaw(registrationId);
        return response.data ?? [];
    }
    /**
     * Get sessions the current facilitator runs with full response, optionally scoped to a single
     * registration (exact server-side filter).
     * @param registrationId - Restrict to sessions on this registration
     */
    mySessionsRaw(registrationId) {
        const url = this.buildUrl(registrationId != null ? `my-sessions?registrationId=${registrationId}` : 'my-sessions');
        return this.http.get(url);
    }
    /**
     * Get dashboard statistics for the current facilitator
     * @returns Facilitator dashboard statistics
     */
    async dashboardStats() {
        const response = await this.dashboardStatsRaw();
        if (!response.data) {
            throw new Error('Failed to retrieve facilitator dashboard stats');
        }
        return response.data;
    }
    /**
     * Get dashboard statistics for the current facilitator with full response
     * @returns ResponseData with facilitator dashboard statistics
     */
    dashboardStatsRaw() {
        const url = this.buildUrl('dashboard-stats');
        return this.http.get(url);
    }
    /**
     * Get participants on a registration (name and contact only)
     * @param registrationId - The registration ID
     * @returns Array of facilitator participant views
     */
    async participants(registrationId) {
        const response = await this.participantsRaw(registrationId);
        return response.data ?? [];
    }
    /**
     * Get participants on a registration with full response
     * @param registrationId - The registration ID
     * @returns ResponseData with array of facilitator participant views
     */
    participantsRaw(registrationId) {
        const url = this.buildUrl(`registrations/${registrationId}/participants`);
        return this.http.get(url);
    }
    /**
     * Update the progress of a registration session
     * @param registrationSessionId - The registration session ID
     * @param data - Update payload (status / progress / date)
     * @returns Updated registration session model view
     */
    async updateSessionProgress(registrationSessionId, data) {
        const response = await this.updateSessionProgressRaw(registrationSessionId, data);
        if (!response.data) {
            throw new Error(`Failed to update progress for registration session ${registrationSessionId}`);
        }
        return response.data;
    }
    /**
     * Update the progress of a registration session with full response
     * @param registrationSessionId - The registration session ID
     * @param data - Update payload
     * @returns ResponseData with updated registration session model view
     */
    updateSessionProgressRaw(registrationSessionId, data) {
        const url = this.buildUrl(`sessions/${registrationSessionId}/progress`);
        return this.http.patch(url, data);
    }
    // ---------------------------------------------------------------------------
    // Session status / progress (new value in the route)
    // ---------------------------------------------------------------------------
    /** Change only the session's status. */
    async changeSessionStatus(registrationSessionId, newStatus) {
        const res = await this.http.put(this.buildUrl(`sessions/${registrationSessionId}/change-status/${newStatus}`));
        if (!res.data)
            throw new Error(`Failed to change status for session ${registrationSessionId}`);
        return res.data;
    }
    /** Change only the session's progress status. */
    async changeSessionProgress(registrationSessionId, newProgressStatus) {
        const res = await this.http.put(this.buildUrl(`sessions/${registrationSessionId}/change-progress/${newProgressStatus}`));
        if (!res.data)
            throw new Error(`Failed to change progress for session ${registrationSessionId}`);
        return res.data;
    }
    // ---------------------------------------------------------------------------
    // Course custom data (fill in values; field definitions are admin-owned)
    // ---------------------------------------------------------------------------
    /** Get the registration's course custom-field values. */
    async courseCustomData(registrationId) {
        const res = await this.http.get(this.buildUrl(`registrations/${registrationId}/course-custom-data`));
        return res.data ?? [];
    }
    /** Bulk-save the registration's course custom-field values (whole-form save). */
    async saveCourseCustomData(registrationId, items) {
        const res = await this.http.put(this.buildUrl(`registrations/${registrationId}/course-custom-data`), items);
        return res.data ?? [];
    }
    // ---------------------------------------------------------------------------
    // Session custom data + the field definitions used to render the form
    // ---------------------------------------------------------------------------
    /** Get the session's custom-field values. */
    async sessionCustomData(registrationSessionId) {
        const res = await this.http.get(this.buildUrl(`sessions/${registrationSessionId}/custom-data`));
        return res.data ?? [];
    }
    /** Save a single session custom-field value. */
    async addSessionCustomData(registrationSessionId, data) {
        const res = await this.http.post(this.buildUrl(`sessions/${registrationSessionId}/custom-data`), data);
        if (!res.data)
            throw new Error(`Failed to save custom data for session ${registrationSessionId}`);
        return res.data;
    }
    /** Bulk-save the session's custom-field values (whole-form save). */
    async saveSessionCustomData(registrationSessionId, items) {
        const res = await this.http.put(this.buildUrl(`sessions/${registrationSessionId}/custom-data`), items);
        return res.data ?? [];
    }
    /**
     * The custom-field definitions for this sitting's template — read-only, used to
     * render the capture form. Empty for ad-hoc sessions with no template link.
     */
    async sessionCustomFields(registrationSessionId) {
        const res = await this.http.get(this.buildUrl(`sessions/${registrationSessionId}/custom-fields`));
        return res.data ?? [];
    }
    // ---------------------------------------------------------------------------
    // Registration notes (admin-only notes are filtered out server-side)
    // ---------------------------------------------------------------------------
    async notes(registrationId) {
        const res = await this.http.get(this.buildUrl(`registrations/${registrationId}/notes`));
        return res.data ?? [];
    }
    async addNote(registrationId, data) {
        const res = await this.http.post(this.buildUrl(`registrations/${registrationId}/notes`), data);
        if (!res.data)
            throw new Error(`Failed to add note to registration ${registrationId}`);
        return res.data;
    }
    /** Update a note you authored. */
    async updateNote(registrationId, noteId, data) {
        const res = await this.http.put(this.buildUrl(`registrations/${registrationId}/notes/${noteId}`), data);
        if (!res.data)
            throw new Error(`Failed to update note ${noteId}`);
        return res.data;
    }
    /** Delete a note you authored. */
    async deleteNote(registrationId, noteId) {
        const res = await this.http.delete(this.buildUrl(`registrations/${registrationId}/notes/${noteId}`));
        return res.success;
    }
    // ---------------------------------------------------------------------------
    // Registration session notes (admin-only notes are withheld server-side; you
    // may only edit/delete notes you authored)
    // ---------------------------------------------------------------------------
    async sessionNotes(registrationSessionId) {
        const res = await this.http.get(this.buildUrl(`sessions/${registrationSessionId}/notes`));
        return res.data ?? [];
    }
    async addSessionNote(registrationSessionId, data) {
        const res = await this.http.post(this.buildUrl(`sessions/${registrationSessionId}/notes`), data);
        if (!res.data)
            throw new Error(`Failed to add note to session ${registrationSessionId}`);
        return res.data;
    }
    async updateSessionNote(registrationSessionId, sessionNoteId, data) {
        const res = await this.http.put(this.buildUrl(`sessions/${registrationSessionId}/notes/${sessionNoteId}`), data);
        if (!res.data)
            throw new Error(`Failed to update session note ${sessionNoteId}`);
        return res.data;
    }
    async deleteSessionNote(registrationSessionId, sessionNoteId) {
        const res = await this.http.delete(this.buildUrl(`sessions/${registrationSessionId}/notes/${sessionNoteId}`));
        return res.success;
    }
    // ---------------------------------------------------------------------------
    // Registration files
    // ---------------------------------------------------------------------------
    async files(registrationId) {
        const res = await this.http.get(this.buildUrl(`registrations/${registrationId}/files`));
        return res.data ?? [];
    }
    async addFile(registrationId, data) {
        const res = await this.http.post(this.buildUrl(`registrations/${registrationId}/files`), data);
        if (!res.data)
            throw new Error(`Failed to add file to registration ${registrationId}`);
        return res.data;
    }
    async deleteFile(registrationId, fileId) {
        const res = await this.http.delete(this.buildUrl(`registrations/${registrationId}/files/${fileId}`));
        return res.success;
    }
}
FacilitatorPortalService.DEFAULT_SERVICE_PATH = 'ac-courses';
FacilitatorPortalService.BASE_PATH = 'api/portal/facilitator';
//# sourceMappingURL=FacilitatorPortalService.js.map